import { coverageTargetId, creditCoverageThresholdsAt, unmeasuredThresholdsAt } from "./creditScopes";
/**
 * How one requirement of a pursued credit reads. Design: #27 §6. Evidence reads
 * Submitted / Outstanding; Performance reads the other states.
 */
export var RequirementState;
(function (RequirementState) {
    RequirementState["Submitted"] = "submitted";
    RequirementState["Outstanding"] = "outstanding";
    RequirementState["OnTrack"] = "on-track";
    /**
     * On pace, but achievement is short of the pursued level's figure. Neither
     * passing nor failing, so the credit reads Not Verified (`decisions.md` §22).
     */
    RequirementState["OnPaceNotMet"] = "on-pace-not-met";
    RequirementState["AtRisk"] = "at-risk";
    /** Figures are in with no threshold; counts toward Supported. */
    RequirementState["Reported"] = "reported";
    /** Nothing to judge yet (no pace or no figures); credit reads Not Verified. */
    RequirementState["AwaitingReport"] = "awaiting-report";
    /** Switched off here; the obligation has not moved, only our view of it. */
    RequirementState["TrackedElsewhere"] = "tracked-elsewhere";
})(RequirementState || (RequirementState = {}));
export var CreditState;
(function (CreditState) {
    CreditState["Supported"] = "supported";
    CreditState["AtRisk"] = "at-risk";
    /**
     * Everything is in and on pace, but a measured figure is still short of the
     * level's threshold: supported once it reaches it. Earns no points at the pursued level.
     */
    CreditState["OnPace"] = "on-pace";
    /** Nothing failing, but something we cannot see or cannot measure. */
    CreditState["NotVerified"] = "not-verified";
})(CreditState || (CreditState = {}));
const PASSING = new Set([
    RequirementState.Submitted,
    RequirementState.OnTrack,
    RequirementState.Reported
]);
const FAILING = new Set([
    RequirementState.Outstanding,
    RequirementState.AtRisk
]);
/**
 * A switched-off requirement stays in and keeps the credit from Supported. A
 * level threshold no Performance requirement measures is never Supported on
 * uploads alone, nor is a credit missing the requirement of any coverage
 * figure at or below its level ({@link coverageTargetId}), nor one with a
 * threshold Catchment cannot count ({@link unmeasuredThresholdsAt}).
 */
export const creditStateFor = (scope, credit, levelId, requirements) => {
    if (requirements.some((requirement) => FAILING.has(requirement.state))) {
        return CreditState.AtRisk;
    }
    const thresholdUnmeasured = (credit.levelValues?.[levelId] != null &&
        !requirements.some((requirement) => requirement.track === "performance")) ||
        creditCoverageThresholdsAt(scope, credit, levelId).some((coverage) => !requirements.some((requirement) => requirement.targetId ===
            coverageTargetId(credit.creditCode, coverage.levelId))) ||
        unmeasuredThresholdsAt(scope, credit, levelId).length > 0;
    if (thresholdUnmeasured || requirements.length === 0) {
        return CreditState.NotVerified;
    }
    const short = requirements.filter((requirement) => !PASSING.has(requirement.state));
    if (short.length === 0)
        return CreditState.Supported;
    // Only on-pace figures stand between the credit and Supported.
    return short.every((requirement) => requirement.state === RequirementState.OnPaceNotMet)
        ? CreditState.OnPace
        : CreditState.NotVerified;
};
